/*
 * TimingEditor — extracted from Production.jsx for maintainability.
 * Modal used to edit a production batch's stage timings, actual output,
 * wastage, rework material list, and remark. Admin users can also rename
 * the batch ID (cascades to QC / tasks / FG refs).
 *
 * Props:
 *   batch       — the batch object being edited
 *   onClose     — close the modal
 *   onSave      — (batchId, patch) => Promise<void> to persist changes
 *   isAdmin     — enables the rename button
 *   onRenamed   — (oldId, newId) => void; fired after a successful rename
 */
import React from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, Plus, Edit3 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const STAGES = [
  { key: 'pre_blending', label: 'Pre Blending' },
  { key: 'grinding', label: 'Grinding' },
  { key: 'mixing', label: 'Mixing' },
  { key: 'sieving', label: 'Sieving' },
  { key: 'inside_time', label: 'Inside Time' },
  { key: 'outside_adding', label: 'Outside Adding' },
  { key: 'cleaning', label: 'Cleaning' },
  { key: 'shifter', label: 'Shifter' },
  { key: 'packing', label: 'Packing' },
];

export default function TimingEditor({ batch, onClose, onSave, isAdmin, onRenamed }) {
  const [timings, setTimings] = React.useState(() => {
    const t = batch.timings || {};
    const parsed = {};
    STAGES.forEach(({ key }) => {
      const val = t[key] || '';
      const parts = val.split('-').map(s => s.trim());
      parsed[key] = { start: parts[0] || '', end: parts[1] || '' };
    });
    return parsed;
  });
  const [actualOutput, setActualOutput] = React.useState(batch.actual_output_kg || '');
  const [remark, setRemark] = React.useState(batch.remark || '');
  const [reworkMaterials, setReworkMaterials] = React.useState(batch.rework_materials || []);
  // iter186 — expected output: live = batch_size_kg + Σ rework, unless user overrides it
  const [overrideMode, setOverrideMode] = React.useState(
    batch.expected_output_override !== null && batch.expected_output_override !== undefined,
  );
  const [overrideValue, setOverrideValue] = React.useState(
    batch.expected_output_override ?? '',
  );

  const reworkSum = React.useMemo(
    () => reworkMaterials.reduce((s, rm) => s + (parseFloat(rm.qty_kg) || 0), 0),
    [reworkMaterials],
  );
  const autoExpected = (batch.batch_size_kg || batch.total_output_kg || 0) + reworkSum;
  const effectiveExpected = overrideMode && overrideValue !== ''
    ? parseFloat(overrideValue) || 0
    : autoExpected;

  // ─── Batch ID rename (admin-only) ───
  const [renameMode, setRenameMode] = React.useState(false);
  const [newId, setNewId] = React.useState(batch.id);
  const [renaming, setRenaming] = React.useState(false);

  const handleRename = async () => {
    const target = (newId || '').trim();
    if (!target || target === batch.id) { setRenameMode(false); return; }
    if (!window.confirm(`Rename batch "${batch.id}" → "${target}"?\n\nThis will cascade-update QC rows, production tasks, and finished-good references.`)) return;
    setRenaming(true);
    try {
      await axios.patch(`${API}/production/batches/${batch.id}/override-id`, { new_id: target }, { withCredentials: true });
      toast.success(`Batch renamed to ${target}`);
      setRenameMode(false);
      onRenamed?.(batch.id, target);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Rename failed');
    } finally {
      setRenaming(false);
    }
  };

  const buildTimingsPayload = () => {
    const result = {};
    STAGES.forEach(({ key }) => {
      const { start, end } = timings[key] || {};
      result[key] = start && end ? `${start}-${end}` : start || '';
    });
    return result;
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-zinc-900 border border-zinc-700 rounded-2xl w-full max-w-lg max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="timing-editor">
        <div className="px-5 py-4 border-b border-zinc-800 flex items-center justify-between sticky top-0 bg-zinc-900 z-10">
          <div className="flex-1 min-w-0">
            {renameMode ? (
              <div className="flex items-center gap-1.5">
                <span className="text-zinc-500 text-[11px] shrink-0">Rename to:</span>
                <input
                  data-testid="batch-rename-input"
                  value={newId}
                  onChange={e => setNewId(e.target.value)}
                  autoFocus
                  className="bg-zinc-800 border border-amber-500 rounded px-2 py-1 text-zinc-50 text-sm flex-1 min-w-0 focus:outline-none"
                  placeholder="e.g. 260422A"
                />
                <button
                  data-testid="batch-rename-save"
                  onClick={handleRename}
                  disabled={renaming}
                  className="px-2 py-1 rounded bg-amber-500 hover:bg-amber-400 disabled:bg-zinc-700 text-black text-[11px] font-semibold"
                >{renaming ? '…' : 'Save'}</button>
                <button
                  onClick={() => { setRenameMode(false); setNewId(batch.id); }}
                  className="px-2 py-1 rounded bg-zinc-800 text-zinc-400 text-[11px]"
                >Cancel</button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <h3 className="font-heading font-bold text-zinc-50 text-sm">Batch Details — <span className="font-mono">{batch.id}</span></h3>
                {isAdmin && (
                  <button
                    data-testid="batch-rename-btn"
                    onClick={() => setRenameMode(true)}
                    title="Rename batch ID (cascades to QC/tasks/FG refs)"
                    className="p-1 rounded hover:bg-zinc-800 text-zinc-500 hover:text-amber-400 transition-colors"
                  >
                    <Edit3 size={12} />
                  </button>
                )}
              </div>
            )}
            <p className="text-zinc-500 text-[10px] mt-0.5">{batch.product_name} · {batch.num_batches}x{batch.batch_size_kg}kg · {batch.status}</p>
          </div>
          <button onClick={onClose}><X size={18} className="text-zinc-500 hover:text-zinc-200" /></button>
        </div>
        <div className="p-5 space-y-4">
          {/* Stage Timings with proper time inputs */}
          <div>
            <label className="text-zinc-400 text-xs font-semibold mb-2 block">Production Stage Timings</label>
            <div className="space-y-1.5">
              <div className="grid grid-cols-[1fr_80px_80px] gap-1 text-[9px] text-zinc-600 px-1">
                <span>Stage</span><span className="text-center">Start</span><span className="text-center">End</span>
              </div>
              {STAGES.map(({ key, label }) => (
                <div key={key} className="grid grid-cols-[1fr_80px_80px] gap-1 items-center">
                  <label className="text-zinc-400 text-[10px] px-1">{label}</label>
                  <input type="time" value={timings[key]?.start || ''} onChange={e => setTimings(t => ({ ...t, [key]: { ...t[key], start: e.target.value } }))}
                    className="bg-zinc-800 border border-zinc-700 rounded-lg px-1.5 py-1 text-zinc-200 text-[10px] focus:outline-none focus:border-amber-500" />
                  <input type="time" value={timings[key]?.end || ''} onChange={e => setTimings(t => ({ ...t, [key]: { ...t[key], end: e.target.value } }))}
                    className="bg-zinc-800 border border-zinc-700 rounded-lg px-1.5 py-1 text-zinc-200 text-[10px] focus:outline-none focus:border-amber-500" />
                </div>
              ))}
            </div>
          </div>
          {/* Output & Wastage */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-zinc-400 text-xs mb-1 block">Actual Output (kg)</label>
              <input type="number" value={actualOutput} onChange={e => setActualOutput(e.target.value)}
                placeholder={`Expected: ${effectiveExpected.toFixed(2)}`}
                data-testid="actual-output-input"
                className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2 text-zinc-50 text-sm focus:outline-none focus:border-amber-500" />
              {actualOutput && parseFloat(actualOutput) < effectiveExpected && (
                <p className="text-red-400 text-[10px] mt-1">Wastage: {(effectiveExpected - parseFloat(actualOutput)).toFixed(2)}kg ({((effectiveExpected - parseFloat(actualOutput)) / effectiveExpected * 100).toFixed(1)}%)</p>
              )}
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-zinc-400 text-xs">Expected Output</label>
                <button
                  type="button"
                  data-testid="expected-override-toggle"
                  onClick={() => {
                    if (overrideMode) {
                      setOverrideMode(false);
                      setOverrideValue('');
                    } else {
                      setOverrideMode(true);
                      setOverrideValue(autoExpected.toFixed(2));
                    }
                  }}
                  className={`text-[9px] font-semibold px-1.5 py-0.5 rounded border ${overrideMode ? 'bg-amber-500 text-black border-amber-500' : 'bg-zinc-800 text-zinc-400 border-zinc-700 hover:text-zinc-100'}`}>
                  {overrideMode ? '✓ Manual' : '✎ Override'}
                </button>
              </div>
              {overrideMode ? (
                <input
                  type="number"
                  step="any"
                  value={overrideValue}
                  onChange={e => setOverrideValue(e.target.value)}
                  data-testid="expected-override-input"
                  className="w-full bg-zinc-800 border border-amber-700 rounded-xl px-3 py-2 text-amber-300 font-mono text-sm focus:outline-none focus:border-amber-500"
                  placeholder={autoExpected.toFixed(2)}
                />
              ) : (
                <div className="w-full bg-zinc-800/50 border border-zinc-700 rounded-xl px-3 py-2 text-zinc-300 font-mono text-sm" data-testid="expected-output-display">
                  {effectiveExpected.toFixed(2)} kg
                </div>
              )}
              <p className="text-[9px] text-zinc-500 mt-1">
                Recipe {(batch.batch_size_kg || batch.total_output_kg || 0).toFixed(2)} kg{reworkSum > 0 ? ` + Rework ${reworkSum.toFixed(2)} kg` : ''}{overrideMode ? ' · Overridden' : ''}
              </p>
            </div>
          </div>
          {/* Rework / Old Material Added */}
          <div>
            <label className="text-zinc-400 text-xs font-semibold mb-1.5 block">Rework Material (Old Product Added)</label>
            <div className="space-y-1">
              {reworkMaterials.map((rm, i) => (
                <div key={i} className="flex gap-1 items-center">
                  <input value={rm.name || ''} onChange={e => { const n = [...reworkMaterials]; n[i] = { ...rm, name: e.target.value }; setReworkMaterials(n); }}
                    placeholder="Material name" className="flex-1 bg-zinc-800 border border-zinc-700 rounded-lg px-2 py-1 text-zinc-200 text-[10px] focus:outline-none" />
                  <input type="number" value={rm.qty_kg || ''} onChange={e => { const n = [...reworkMaterials]; n[i] = { ...rm, qty_kg: e.target.value }; setReworkMaterials(n); }}
                    placeholder="Kg" className="w-16 bg-zinc-800 border border-zinc-700 rounded-lg px-2 py-1 text-zinc-200 text-[10px] focus:outline-none" />
                  <input type="date" value={rm.mfg_date || ''} onChange={e => { const n = [...reworkMaterials]; n[i] = { ...rm, mfg_date: e.target.value }; setReworkMaterials(n); }}
                    className="w-28 bg-zinc-800 border border-zinc-700 rounded-lg px-1.5 py-1 text-zinc-200 text-[10px] focus:outline-none" />
                  <button onClick={() => setReworkMaterials(reworkMaterials.filter((_, j) => j !== i))}><X size={10} className="text-zinc-600" /></button>
                </div>
              ))}
              <button onClick={() => setReworkMaterials([...reworkMaterials, { name: '', qty_kg: '', mfg_date: '', batch_id: '' }])}
                className="text-amber-400 text-[10px] flex items-center gap-1"><Plus size={10} /> Add Rework Material</button>
            </div>
          </div>
          {/* Remark */}
          <div>
            <label className="text-zinc-400 text-xs mb-1 block">Remark</label>
            <textarea rows={2} value={remark} onChange={e => setRemark(e.target.value)}
              className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2 text-zinc-50 text-sm focus:outline-none focus:border-amber-500 resize-none"
              placeholder="Notes, observations..." />
          </div>
          {/* Save */}
          <div className="flex gap-3 pt-2">
            <button onClick={onClose} className="flex-1 py-2.5 rounded-xl border border-zinc-700 text-zinc-400 text-sm">Cancel</button>
            <button onClick={() => onSave(batch.id, {
              timings: buildTimingsPayload(),
              actual_output_kg: parseFloat(actualOutput) || 0,
              remark,
              rework_materials: reworkMaterials.filter(rm => rm.name),
              expected_output_override: overrideMode && overrideValue !== '' ? parseFloat(overrideValue) : null,
            })}
              data-testid="save-batch-details"
              className="flex-1 py-2.5 rounded-xl bg-amber-500 hover:bg-amber-400 text-black font-semibold text-sm">Save</button>
          </div>
        </div>
      </div>
    </div>
  );
}
